/**
 * TUVI - Parser de Excel
 * Extrae datos de archivos de cotización formato TUVI.xlsx
 *
 * Estructura esperada:
 * - Una sola pestaña (Hoja1)
 * - Datos cliente: C16 (nombre), C17 (correo), C18 (teléfono)
 * - Datos inmueble: G16 (proyecto), G17 (área), G18 (apartamento)
 * - 8 Capítulos fijos en filas: 21, 132, 208, 237, 334, 353, 371, 382
 * - Totales: G389 (subtotal), G390 (AIU), G391 (IVA), G393 (valor final)
 */

import * as XLSX from 'xlsx';

// Configuración de los 8 capítulos fijos
const CAPITULOS_CONFIG = [
  { numero: 1, nombre: 'MORTERO / MAMPOSTERIA / ENCHAPE', filaInicio: 21, filaFin: 131 },
  { numero: 2, nombre: 'ESTUCO Y PINTURA', filaInicio: 132, filaFin: 207 },
  { numero: 3, nombre: 'PLOMERIA', filaInicio: 208, filaFin: 236 },
  { numero: 4, nombre: 'ELECTRICO', filaInicio: 237, filaFin: 333 },
  { numero: 5, nombre: 'CARPINTERIA', filaInicio: 334, filaFin: 352 },
  { numero: 6, nombre: 'MESONES', filaInicio: 353, filaFin: 370 },
  { numero: 7, nombre: 'CABINAS Y ESPEJOS', filaInicio: 371, filaFin: 381 },
  { numero: 8, nombre: 'FINALES', filaInicio: 382, filaFin: 388 }
];

// Posiciones fijas de celdas
const CELDAS = {
  // Datos del cliente
  nombre: 'C16',
  correo: 'C17',
  telefono: 'C18',
  // Datos del inmueble
  proyecto: 'G16',
  area: 'G17',
  apartamento: 'G18',
  // Totales
  subtotal: 'G389',
  aiu: 'G390',
  iva: 'G391',
  valorFinal: 'G393'
};

/**
 * Parsear archivo Excel de cotización formato TUVI
 * @param {File} file Archivo Excel
 * @returns {Promise} Datos extraídos
 */
export const parseExcelCotizacion = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Obtener la primera (y única) pestaña
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // ==========================================
        // DATOS DEL PROYECTO
        // ==========================================
        const proyecto = {
          nombre_cliente: getCellString(sheet, CELDAS.nombre),
          correo: getCellString(sheet, CELDAS.correo),
          telefono: getCellString(sheet, CELDAS.telefono),
          proyecto_nombre: getCellString(sheet, CELDAS.proyecto),
          area_m2: getCellNumber(sheet, CELDAS.area),
          apartamento: getCellString(sheet, CELDAS.apartamento),
          subtotal: getCellNumber(sheet, CELDAS.subtotal),
          aiu: getCellNumber(sheet, CELDAS.aiu),
          iva: getCellNumber(sheet, CELDAS.iva),
          valor_final: getCellNumber(sheet, CELDAS.valorFinal),
          estado: 'contratado_no_iniciado'
        };

        // ==========================================
        // CAPÍTULOS Y ACTIVIDADES
        // ==========================================
        const capitulos = [];

        CAPITULOS_CONFIG.forEach((config) => {
          // Obtener subtotal del capítulo (columna G en la fila de inicio)
          const subtotalCapitulo = getCellNumber(sheet, `G${config.filaInicio}`);

          // Calcular porcentaje respecto al subtotal del proyecto
          const porcentaje = proyecto.subtotal > 0
            ? (subtotalCapitulo / proyecto.subtotal) * 100
            : 0;

          // Extraer actividades del capítulo
          const actividades = [];

          // Las actividades empiezan 2 filas después del título del capítulo
          for (let fila = config.filaInicio + 2; fila <= config.filaFin; fila++) {
            const descripcion = getCellString(sheet, `B${fila}`);
            const valor = getCellNumber(sheet, `H${fila}`);

            // Solo agregar si tiene descripción y valor
            if (descripcion && valor > 0) {
              actividades.push({
                descripcion: descripcion,
                valor: valor,
                orden: actividades.length + 1
              });
            }
          }

          capitulos.push({
            numero: config.numero,
            nombre: config.nombre,
            subtotal: subtotalCapitulo,
            porcentaje: Math.round(porcentaje * 100) / 100, // Redondear a 2 decimales
            actividades: actividades
          });
        });

        resolve({
          proyecto,
          capitulos
        });

      } catch (error) {
        reject(new Error('Error al procesar el archivo: ' + error.message));
      }
    };

    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Obtener valor de celda como string
 * @param {Object} sheet Hoja de Excel
 * @param {string} cellAddress Dirección de celda (ej: 'C16')
 * @returns {string} Valor como string
 */
const getCellString = (sheet, cellAddress) => {
  const cell = sheet[cellAddress];
  if (!cell || cell.v === undefined || cell.v === null) return '';
  return String(cell.v).trim();
};

/**
 * Obtener valor de celda como número
 * @param {Object} sheet Hoja de Excel
 * @param {string} cellAddress Dirección de celda
 * @returns {number} Valor numérico
 */
const getCellNumber = (sheet, cellAddress) => {
  const cell = sheet[cellAddress];
  if (!cell || cell.v === undefined || cell.v === null) return 0;

  // Si es string, intentar parsear
  if (typeof cell.v === 'string') {
    // Remover caracteres no numéricos excepto punto y coma
    const cleaned = cell.v.replace(/[^\d.,]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  return parseFloat(cell.v) || 0;
};

export default parseExcelCotizacion;
